import React from "react";
import { observer } from "mobx-react-lite";
import { List, Button, Space, Typography } from "antd";
import { EyeOutlined } from "@ant-design/icons";
import WorkbenchWidgetCard from "./WorkbenchWidgetCard";
import { useWorkbenchStore } from "../stores/context";
import { Utils } from "../../common/misc/Utils";
import AttachmentIcon from "../../tasks/components/AttachmentIcon";
import AlphaIdLabel from "../../tasks/components/AlphaIdLabel";
import DocumentPreviewDialog from "../../documents/components/DocumentPreviewDialog";

const DocumentsWidget: React.FC = () => {
    const { workbenchStore } = useWorkbenchStore();
    const [previewId, setPreviewId] = React.useState<string | undefined>(
        undefined,
    );

    const disabledForPreview = (fileName: string) => {
        return ![
            ".pdf",
            ".xml",
            ".docx",
            ".xlsx",
            ".xlsm",
            "txt",
            "jpg",
            "png",
        ].some((ext) => fileName.toLocaleLowerCase().endsWith(ext));
    };

    const renderDocumentsContent = () => {
        if (!workbenchStore.documentsData.length) {
            return <div>No documents found</div>;
        }

        return (
            <List
                size="small"
                dataSource={workbenchStore.documentsData}
                renderItem={(document) => (
                    <List.Item
                        actions={
                            disabledForPreview(document.fileName)
                                ? []
                                : [
                                      <Button
                                          type="text"
                                          size="small"
                                          icon={<EyeOutlined />}
                                          key="view"
                                          onClick={() =>
                                              setPreviewId(document.packageId)
                                          }
                                      >
                                          View
                                      </Button>,
                                  ]
                        }
                    >
                        <List.Item.Meta
                            avatar={
                                <AttachmentIcon fileName={document.fileName} />
                            }
                            title={
                                <>
                                    {document.fileName || "Untitled Document"}
                                    <span
                                        style={{
                                            color: "#888",
                                            fontSize: "12px",
                                            fontWeight: "normal",
                                            marginLeft: "8px",
                                        }}
                                    >
                                        {Utils.readableFileSize(
                                            document.fileSize,
                                            true,
                                        ) || "Unknown size"}
                                    </span>
                                </>
                            }
                            description={
                                <Space>
                                    {document.alphaId ? (
                                        <AlphaIdLabel
                                            alphaId={document.alphaId}
                                        />
                                    ) : null}
                                    <Typography.Link
                                        href={`${window.location.origin}/tasks/${document.taskId}`}
                                        target="_blank"
                                    >
                                        {document.taskTitle}
                                    </Typography.Link>
                                </Space>
                            }
                        />
                    </List.Item>
                )}
            />
        );
    };

    return (
        <>
            <DocumentPreviewDialog
                isVisible={!!previewId}
                packageId={previewId}
                onCloseDialog={() => setPreviewId(undefined)}
                packageName={
                    workbenchStore.documentsData.find(
                        (doc) => doc.packageId === previewId,
                    )?.fileName
                }
                onDownload={workbenchStore.handleAttachmentDownload}
            />
            <WorkbenchWidgetCard
                title="Documents"
                widgetState={workbenchStore.documentsWidget}
            >
                {renderDocumentsContent()}
            </WorkbenchWidgetCard>
        </>
    );
};

export default observer(DocumentsWidget);
